import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, Phone, Mail, MapPin, X } from 'lucide-react';

const Footer: React.FC = () => {
  const [selectedFacilityImage, setSelectedFacilityImage] = React.useState<{ src: string, alt: string } | null>(null);

  const schedule = [
    { day: 'Monday', time: '08:00 AM — 05:00 PM' },
    { day: 'Tuesday', time: '08:00 AM — 05:00 PM' },
    { day: 'Wednesday', time: '08:00 AM — 05:00 PM' },
    { day: 'Thursday', time: '08:00 AM — 05:00 PM' },
    { day: 'Friday', time: '08:00 AM — 05:00 PM' },
    { day: 'Saturday', time: '08:00 AM — 12:00 NN' },
    { day: 'Sunday', time: 'Closed' },
  ];

  const facilities = [
    { src: '/img/facilities/facility_1.jpg', alt: 'Medical Examination Room' },
    { src: '/img/facilities/facility_2.jpg', alt: 'Laboratory Equipment' },
    { src: '/img/facilities/facility_3.jpg', alt: 'X-Ray Facility' },
    { src: '/img/facilities/facility_4.jpg', alt: 'Waiting Area' },
    { src: '/img/facilities/facility_5.jpg', alt: 'Reception Area' },
    { src: '/img/facilities/facility_6.jpg', alt: 'Consultation Room' },
  ];

  const navigationItems = [
    { path: '/', label: 'Home' },
    { path: '/about-us', label: 'About Us' },
    { path: '/services', label: 'Services' },
    { path: '/branches', label: 'Branches' },
    { path: '/contact-us', label: 'Contact Us' }
  ];

  return (
    <footer className="bg-blue-900 text-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Clinic Schedule */}
          <div>
            <h4 className="text-xl font-semibold mb-6 flex items-center">
              <Clock className="h-5 w-5 mr-2" />
              Clinic Schedule
            </h4>
            <div className="space-y-3">
              {schedule.map((item, index) => (
                <div key={index} className="flex justify-between items-center py-1 border-b border-blue-800 border-opacity-30">
                  <span className="font-medium">{item.day}</span>
                  <span className={`text-sm ${item.time === 'Closed' ? 'text-red-300' : 'text-blue-200'}`}>
                    {item.time}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h4 className="text-xl font-semibold mb-6">For Inquiries</h4>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <Phone className="h-5 w-5 mt-1 text-blue-300" />
                <div>
                  <p className="text-blue-200">Direct Line:</p>
                  <p className="text-blue-200">(+63) (2) 8523-7832</p>
                  <p className="text-blue-200">(+63) (2) 8554-0539</p>
                  <p className="text-blue-200">(+63) (2) 8524-0627</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Mail className="h-5 w-5 text-blue-300" />
                <a href="mailto:manila@pdsclinics.com" className="text-blue-200 hover:text-white transition-colors">
                  manila@pdsclinics.com
                </a>
              </div>

              <div className="flex items-start space-x-3">
                <MapPin className="h-5 w-5 mt-1 text-blue-300" />
                <div>
                  <p className="text-blue-200 leading-relaxed">
                    Physicians' Tower,<br />
                    533 United Nations Avenue<br />
                    cor. San Carlos St., Ermita<br />
                    Manila, Philippines
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Facilities */}
          <div>
            <h4 className="text-xl font-semibold mb-6">Facilities</h4>
            <div className="grid grid-cols-3 gap-3">

              <div
                className="relative group cursor-pointer"
                onClick={() => setSelectedFacilityImage({ src: "/img/facilities/facility_1.jpg", alt: "X-ray facility" })}
              >
                <img
                  src="/img/facilities/facility_1.jpg"
                  alt="X-ray facility"
                  className="w-full h-20 object-cover rounded-lg group-hover:opacity-80 transition-opacity duration-200"
                />
                <div className="absolute inset-0 bg-blue-600 bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-all duration-200"></div>
              </div>

              <div
                className="relative group cursor-pointer"
                onClick={() => setSelectedFacilityImage({ src: "/img/facilities/facility_2.jpg", alt: "Treadmill Stress Test" })}
              >
                <img
                  src="/img/facilities/facility_2.jpg"
                  alt="Treadmill Stress Test"
                  className="w-full h-20 object-cover rounded-lg group-hover:opacity-80 transition-opacity duration-200"
                />
                <div className="absolute inset-0 bg-blue-600 bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-all duration-200"></div>
              </div>

              <div
                className="relative group cursor-pointer"
                onClick={() => setSelectedFacilityImage({ src: "/img/facilities/facility_3.jpg", alt: "Ultrasound service" })}
              >
                <img
                  src="/img/facilities/facility_3.jpg"
                  alt="Ultrasound service"
                  className="w-full h-20 object-cover rounded-lg group-hover:opacity-80 transition-opacity duration-200"
                />
                <div className="absolute inset-0 bg-blue-600 bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-all duration-200"></div>
              </div>

              <div
                className="relative group cursor-pointer"
                onClick={() => setSelectedFacilityImage({ src: "/img/facilities/facility_4.jpg", alt: "Laboratory Machines" })}
              >
                <img
                  src="/img/facilities/facility_4.jpg"
                  alt="Laboratory Machines"
                  className="w-full h-20 object-cover rounded-lg group-hover:opacity-80 transition-opacity duration-200"
                />
                <div className="absolute inset-0 bg-blue-600 bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-all duration-200"></div>
              </div>

              <div
                className="relative group cursor-pointer"
                onClick={() => setSelectedFacilityImage({ src: "/img/facilities/facility_5.jpg", alt: "X-ray area" })}
              >
                <img
                  src="/img/facilities/facility_5.jpg"
                  alt="X-ray area"
                  className="w-full h-20 object-cover rounded-lg group-hover:opacity-80 transition-opacity duration-200"
                />
                <div className="absolute inset-0 bg-blue-600 bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-all duration-200"></div>
              </div>

              <div
                className="relative group cursor-pointer"
                onClick={() => setSelectedFacilityImage({ src: "/img/facilities/facility_6.jpg", alt: "Audio facility" })}
              >
                <img
                  src="/img/facilities/facility_6.jpg"
                  alt="Audio facility"
                  className="w-full h-20 object-cover rounded-lg group-hover:opacity-80 transition-opacity duration-200"
                />
                <div className="absolute inset-0 bg-blue-600 bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-all duration-200"></div>
              </div>

              <div
                className="relative group cursor-pointer"
                onClick={() => setSelectedFacilityImage({ src: "/img/facilities/facility_7.png", alt: "Dental facility" })}
              >
                <img
                  src="/img/facilities/facility_7.png"
                  alt="Dental facility"
                  className="w-full h-20 object-cover rounded-lg group-hover:opacity-80 transition-opacity duration-200"
                />
                <div className="absolute inset-0 bg-blue-600 bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-all duration-200"></div>
              </div>

              <div
                className="relative group cursor-pointer"
                onClick={() => setSelectedFacilityImage({ src: "/img/facilities/facility_8.png", alt: "Physical examination rooms" })}
              >
                <img
                  src="/img/facilities/facility_8.png"
                  alt="Physical examination rooms"
                  className="w-full h-20 object-cover rounded-lg group-hover:opacity-80 transition-opacity duration-200"
                />
                <div className="absolute inset-0 bg-blue-600 bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-all duration-200"></div>
              </div>

              <div
                className="relative group cursor-pointer"
                onClick={() => setSelectedFacilityImage({ src: "/img/facilities/facility_9.jpg", alt: "X-ray services" })}
              >
                <img
                  src="/img/facilities/facility_9.jpg"
                  alt="X-ray services"
                  className="w-full h-20 object-cover rounded-lg group-hover:opacity-80 transition-opacity duration-200"
                />
                <div className="absolute inset-0 bg-blue-600 bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-all duration-200"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Bottom */}
      <div className="bg-blue-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            {/* Logo */}
            <div className="text-center lg:text-left">
              <img
                src="/img/logos/pds_logo.svg"
                alt="PDSC Logo"
                className="h-12 w-12 mx-auto lg:mx-0"
              />
            </div>

            {/* Navigation */}
            <div className="lg:col-span-2">
              <div className="hidden md:flex flex-wrap justify-center lg:justify-end items-center space-x-1 text-sm">
                {navigationItems.map((item, index) => (
                  <React.Fragment key={item.path}>
                    <Link
                      to={item.path}
                      className="text-blue-200 hover:text-white transition-colors px-2 py-1"
                    >
                      {item.label}
                    </Link>
                    {index < navigationItems.length - 1 && (
                      <span className="text-blue-400">|</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
              <p className="text-blue-200 text-sm text-center lg:text-right mt-4">
                © 2025 Physicians' Diagnostic Services Center, Inc
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Facility Image Modal */}
      {selectedFacilityImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedFacilityImage(null)}
        >
          <div className="relative w-full h-full max-w-screen max-h-screen bg-white rounded-lg flex flex-col justify-center items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedFacilityImage(null)}
              className="absolute top-4 right-4 z-10 bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full p-2 transition-all duration-200"
            >
              <X className="w-6 h-6 text-gray-700" />
            </button>
            <div className="p-8">
              <img
                src={selectedFacilityImage.src}
                alt={selectedFacilityImage.alt}
                className="max-w-[90vw] max-h-[90vh] object-contain"
              />
              <h3 className="text-xl font-semibold text-center mt-4 text-gray-900">
                {selectedFacilityImage.alt}
              </h3>
            </div>
          </div>
        </div>
      )}
    </footer>
  );
};

export default Footer;